import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Banner,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "@magarac/ui";
import { quoteSchema } from "@magarac/shared/schemas/documents";
import { jobDetailSchema } from "@magarac/shared/schemas/job";
import { jobTypeListSchema } from "@magarac/shared/schemas/jobType";
import { requestResponseSchema } from "@magarac/shared/schemas/request";
import { z } from "zod";
import { HelpIcon } from "../../../components/HelpIcon";
import {
  LineItemsEditor,
  blankLine,
  draftsToLines,
  type LineDraft,
} from "../../../components/LineItemsEditor";
import { api } from "../../../lib/api";
import { parseDollars } from "../../../lib/documents";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/quotes/new")({
  validateSearch: (s: Record<string, unknown>): { jobId?: string; requestId?: string } => ({
    ...(typeof s.jobId === "string" ? { jobId: s.jobId } : {}),
    ...(typeof s.requestId === "string" ? { requestId: s.requestId } : {}),
  }),
  component: NewQuotePage,
});

/** CRM-17: a new quote for a job or a request. Screen key: quotes.edit */
function NewQuotePage() {
  const { jobId, requestId } = Route.useSearch();
  const { active } = useTenant();
  const job = useQuery({
    queryKey: ["job", active?.id, jobId],
    queryFn: () => api.get(`/jobs/${jobId}`, jobDetailSchema),
    enabled: Boolean(active && jobId),
  });
  const request = useQuery({
    queryKey: ["request", active?.id, requestId],
    queryFn: () => api.get(`/requests/${requestId}`, requestResponseSchema),
    enabled: Boolean(active && requestId),
  });
  const jobTypes = useQuery({
    queryKey: ["jobTypes", active?.id],
    queryFn: () => api.get("/job-types", jobTypeListSchema),
    enabled: Boolean(active),
  });

  if (!jobId && !requestId)
    return (
      <Card>
        <p>
          Start a quote from a job or a request: open it and choose <strong>New quote</strong>.
        </p>
        <Link to="/jobs" className="text-brand-700 underline">
          Go to jobs
        </Link>
      </Card>
    );
  if (job.isPending && jobId) return <Spinner />;
  if (request.isPending && requestId) return <Spinner />;
  if (job.error || request.error)
    return <Banner tone="danger">{messageFor(job.error ?? request.error)}</Banner>;

  const customerId = job.data?.job.customerId ?? request.data?.request.customerId ?? null;
  const customerName = job.data?.customer.name ?? request.data?.request.customerName ?? "";
  const jobType = jobTypes.data?.jobTypes.find((t) => t.id === job.data?.job.jobTypeId);
  const firstLine = job.data
    ? blankLine(job.data.jobTypeName, job.data.job.amountDueCents || jobType?.priceCents || 0)
    : blankLine();

  if (!customerId)
    return (
      <Banner tone="warn">
        This request has no customer yet. Add the customer to the request, then make the quote.
      </Banner>
    );
  return (
    <QuoteForm
      key={`${jobId ?? ""}${requestId ?? ""}`}
      customerId={customerId}
      customerName={customerName}
      jobId={jobId}
      requestId={requestId}
      firstLine={firstLine}
      jobTypes={(jobTypes.data?.jobTypes ?? []).filter((t) => t.active)}
    />
  );
}

function QuoteForm(props: {
  customerId: string;
  customerName: string;
  jobId?: string;
  requestId?: string;
  firstLine: LineDraft;
  jobTypes: Array<{ id: string; name: string }>;
}) {
  const navigate = useNavigate();
  const invoicing = useSetting("invoicing").value;
  const [drafts, setDrafts] = useState<LineDraft[]>([props.firstLine]);
  const [deposit, setDeposit] = useState("");
  const [notes, setNotes] = useState("");
  const [jobTypeId, setJobTypeId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (body: unknown) => api.post("/quotes", body, z.object({ quote: quoteSchema })),
    onSuccess: ({ quote }) => navigate({ to: "/quotes/$id", params: { id: quote.id } }),
    onError: (err) => setError(messageFor(err)),
  });
  const save = () => {
    const parsed = draftsToLines(drafts);
    if ("error" in parsed) return setError(parsed.error);
    const depositCents = deposit.trim() ? parseDollars(deposit) : 0;
    if (depositCents === null || depositCents < 0)
      return setError("The deposit must be a dollar amount (or empty for none).");
    setError(null);
    create.mutate({
      customerId: props.customerId,
      ...(props.jobId ? { jobId: props.jobId } : {}),
      ...(props.requestId ? { requestId: props.requestId } : {}),
      ...(jobTypeId ? { jobTypeId } : {}),
      lines: parsed.lines,
      depositCents,
      notes: notes.trim() || null,
    });
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={`New quote for ${props.customerName}`}
        help={<HelpIcon screen="quotes.edit" />}
      />
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Card className="mb-4">
        <LineItemsEditor
          drafts={drafts}
          onChange={setDrafts}
          taxRateBp={invoicing?.taxRateBp ?? 0}
          taxLabel={invoicing?.taxLabel ?? "Sales tax"}
        />
      </Card>
      <Card className="mb-4">
        {props.requestId ? (
          <Field
            id="q-service"
            label="Service"
            hint="When the customer approves, the job is made as this service."
          >
            <Select id="q-service" value={jobTypeId} onChange={(e) => setJobTypeId(e.target.value)}>
              <option value="">Choose a service</option>
              {props.jobTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field
          id="q-deposit"
          label="Deposit to approve (optional)"
          hint="The customer pays this by card right after approving."
        >
          <Input
            id="q-deposit"
            inputMode="decimal"
            placeholder="0.00"
            value={deposit}
            onChange={(e) => setDeposit(e.target.value)}
          />
        </Field>
        <Field id="q-notes" label="Note to the customer (optional)">
          <Textarea
            id="q-notes"
            rows={3}
            maxLength={2000}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </Card>
      <div className="flex gap-2">
        <Button busy={create.isPending} onClick={save}>
          Save draft
        </Button>
      </div>
    </div>
  );
}
